'use client';

import { useState, type ReactNode } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_VALUE_TEXT
} from '@/components/attributes';
import { cn } from '@/lib/utils';

/**
 * Expandable subsection holding one kind of identity variant (LF-5714).
 *
 * Variants used to live in their own top-level "Other names & addresses"
 * section, which restated what the sections around it already said: the host
 * form already has a name section and an address section, so a third section
 * covering both names *and* addresses cut across them. Each kind now folds into
 * the parent section that owns that field — other names under the name fields,
 * other addresses under the address fields — as a subsection one tier down.
 *
 * Collapsed by default: variants are supplementary to the base value above
 * them, and expanding every one of them by default buried the sections that
 * follow.
 */

interface VariantSubsectionProps {
  /** e.g. "Other names". Reads as a subordinate of the parent section heading. */
  label: string;
  /** Add-button label, e.g. "Add name". */
  addLabel: string;
  /** Shown beside the label so a collapsed subsection still declares its contents. */
  count: number;
  onAdd: () => void;
  disabled?: boolean;
  /** Shown in place of rows when there are none and nothing is being added. */
  emptyHint: string;
  isEmpty: boolean;
  children: ReactNode;
}

export function VariantSubsection({
  label,
  addLabel,
  count,
  onAdd,
  disabled = false,
  emptyHint,
  isEmpty,
  children
}: VariantSubsectionProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className="flex items-center gap-1.5 text-left"
        >
          <span
            className={cn(
              ATTRIBUTE_LABEL_TEXT,
              ATTRIBUTE_MUTED_INK,
              'font-medium'
            )}
          >
            {label}
          </span>
          {count > 0 && (
            <span className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
              ({count})
            </span>
          )}
          <ChevronDown
            className={cn(
              'text-muted-foreground h-3.5 w-3.5 transition-transform',
              !expanded && '-rotate-90'
            )}
            aria-hidden="true"
          />
        </button>

        {expanded && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              ATTRIBUTE_LABEL_TEXT,
              'text-muted-foreground hover:text-foreground h-6 gap-1 px-1.5'
            )}
            onClick={onAdd}
            disabled={disabled}
          >
            <Plus className="h-3 w-3" />
            {addLabel}
          </Button>
        )}
      </div>

      {expanded && (
        <div>
          {isEmpty ? (
            <p
              className={cn(ATTRIBUTE_VALUE_TEXT, ATTRIBUTE_MUTED_INK, 'py-1')}
            >
              {emptyHint}
            </p>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
}
